import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Cpu, BookOpen, Shield, Rocket, FolderKanban, PlugZap, ArrowRight } from 'lucide-react'
import { api } from '../api/client'
import { Card } from '../components/ui/Card'

export function DashboardV2() {
  const navigate = useNavigate()

  const { data: catalog } = useQuery({
    queryKey: ['catalog'],
    queryFn: () => api.registry.getCatalog(),
  })

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.projects.list(),
  })

  const totalProjects = projectsData?.projects?.length ?? 0

  return (
    <div style={{ padding: 'var(--space-4)', maxWidth: 1000, margin: '0 auto' }}>
      {/* Welcome */}
      <div style={{ marginBottom: 'var(--space-5)', textAlign: 'center', paddingTop: 'var(--space-4)' }}>
        <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.5px', margin: '0 0 8px' }}>
          KONE AI Context Lake
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 15, margin: '0 auto', maxWidth: 560, lineHeight: 1.6 }}>
          Your engineering knowledge — agents, skills, and rules — delivered to every developer's AI session automatically.
        </p>
      </div>

      {/* Get Started CTA — rotating border button */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-5)' }}>
        <button
          className="glow-border"
          onClick={() => navigate('/quick-start')}
          style={{
            position: 'relative', padding: '14px 32px',
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--text-primary)', fontSize: 16, fontWeight: 600,
            borderRadius: 'var(--radius-s)', letterSpacing: '-0.2px',
            display: 'flex', alignItems: 'center', gap: 10,
          }}
        >
          <Rocket size={18} />
          Get Started — Connect Your Project
          <ArrowRight size={16} />
        </button>
      </div>

      {/* What's available */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 'var(--space-3)', marginBottom: 'var(--space-5)',
      }}>
        <Card style={{ textAlign: 'center', padding: 'var(--space-3)' }}>
          <Cpu size={24} color="var(--brand)" style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--brand)' }}>{catalog?.totals.agents ?? '—'}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>AI Agents</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Expert personas for specific tasks</div>
        </Card>
        <Card style={{ textAlign: 'center', padding: 'var(--space-3)' }}>
          <BookOpen size={24} color="var(--success)" style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--success)' }}>{catalog?.totals.skills ?? '—'}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Skills</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Step-by-step guides agents follow</div>
        </Card>
        <Card style={{ textAlign: 'center', padding: 'var(--space-3)' }}>
          <Shield size={24} color="var(--warning)" style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--warning)' }}>{catalog?.totals.rules ?? '—'}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Rules</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Guardrails AI always obeys</div>
        </Card>
      </div>

      {/* Navigation cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <NavCard
          icon={BookOpen}
          color="var(--brand)"
          title="Browse Catalog"
          description="Explore all agents, skills, and rules available — grouped by team."
          onClick={() => navigate('/catalog')}
        />
        <NavCard
          icon={FolderKanban}
          color="var(--warning)"
          title={`My Projects (${totalProjects})`}
          description="View registered projects and their AI configurations."
          onClick={() => navigate('/projects')}
        />
        <NavCard
          icon={PlugZap}
          color="var(--success)"
          title="MCP Registry"
          description="One-click install MCP servers for your IDE."
          onClick={() => navigate('/mcp-registry')}
        />
        <NavCard
          icon={Rocket}
          color="var(--purple, #7b79fc)"
          title="Quick Start Guide"
          description="Connect a new project in ~5 minutes or set up your personal tools."
          onClick={() => navigate('/quick-start')}
        />
      </div>

      {/* How it works — brief */}
      <Card style={{ marginTop: 'var(--space-5)' }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>How it works</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-3)', textAlign: 'center' }}>
          <div>
            <div style={{ fontSize: 22, marginBottom: 4 }}>1</div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Register</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Add your system to the canonical repo
            </div>
          </div>
          <div>
            <div style={{ fontSize: 22, marginBottom: 4 }}>2</div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Connect</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Commit MCP config + AGENTS.md to your repo
            </div>
          </div>
          <div>
            <div style={{ fontSize: 22, marginBottom: 4 }}>3</div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Code</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Open your IDE — AI gets rules, agents & skills automatically
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}

function NavCard({ icon: Icon, color, title, description, onClick }: Readonly<{
  icon: React.ElementType; color: string; title: string; description: string; onClick: () => void
}>) {
  return (
    <Card
      style={{ cursor: 'pointer', transition: 'border-color 0.15s, transform 0.15s' }}
      onClick={onClick}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 'var(--radius-s)', flexShrink: 0,
          background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={18} color={color} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{description}</div>
        </div>
        <ArrowRight size={14} color="var(--text-muted)" style={{ marginTop: 4 }} />
      </div>
    </Card>
  )
}
